/** Normalize slot / bank question_data for trainer preview. */

import {
  QUESTION_TYPE,
  type QuestionTypeValue,
} from "../../files/projectConfigConstants";

export { QUESTION_TYPE };
export type { QuestionTypeValue };

export type PreviewKind =
  | typeof QUESTION_TYPE.MCQ
  | typeof QUESTION_TYPE.PYTHON_CODING
  | typeof QUESTION_TYPE.SQL_CODING
  | typeof QUESTION_TYPE.FRONTEND_CODING
  | "unknown";

export type CodingPreviewKind =
  | typeof QUESTION_TYPE.PYTHON_CODING
  | typeof QUESTION_TYPE.SQL_CODING
  | typeof QUESTION_TYPE.FRONTEND_CODING;

export type PreviewQuestion = {
  question_id: string;
  question_type?: string;
  level?: string;
  subject_name?: string;
  topic_name?: string;
  sub_topic_name?: string;
  question_types_enum?: string | null;
  question_data?: Record<string, unknown> | null;
};

export type McqPreviewModel = {
  question_id: string;
  question: string;
  options: string[];
  correct_answer: string;
  explanation?: string;
  topic_name?: string;
  sub_topic_name?: string;
  level?: string;
};

export type CodingPreviewModel = {
  kind: CodingPreviewKind;
  question_id: string;
  prompt: string;
  template: string;
  answer: string;
  topic_name?: string;
  sub_topic_name?: string;
  level?: string;
  examples: {
    inputs: string[];
    output: string;
    explanation: string;
  }[];
  testCases: unknown[];
  /** SQL expected rows */
  expectedOutput: Record<string, unknown>[];
  tables: { tab_name: string; data: Record<string, unknown>[] }[];
  table?: string;
  functionCall?: string;
  /** Frontend file tabs */
  tabs?: { name: string; content: string; type?: string }[];
  /** Frontend requirements HTML/text */
  requirements?: string;
  /** Student Code_Validation map used by htmlCss validation utils */
  Code_Validation?: Record<string, unknown>;
  image_urls?: Array<{ actualUrl: string; expectedUrl: string }>;
  /** Frontend expected media (aliases: image_path / video_path) */
  ExpectedImage?: string;
  ExpectedVideo?: string;
  /** Frontend ExpectedOutput may be string/HTML or structured */
  ExpectedOutput?: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item));
};

const normalizeEnum = (value: unknown): string =>
  String(value || "")
    .toLowerCase()
    .replace(/^question_types\./, "");

const fileTypeFromName = (name: string): string => {
  const ext = name.split(".").pop()?.toLowerCase();
  return ext || "text";
};

export const resolvePreviewKind = (q: PreviewQuestion): PreviewKind => {
  const type = normalizeEnum(q.question_type);
  const enumRaw = normalizeEnum(q.question_types_enum);
  const data = asRecord(q.question_data) || {};

  if (type === QUESTION_TYPE.MCQ || enumRaw === QUESTION_TYPE.MCQ) {
    return QUESTION_TYPE.MCQ;
  }
  if (
    enumRaw === QUESTION_TYPE.SQL_CODING ||
    enumRaw.includes("sql") ||
    data.Table ||
    data.Query
  ) {
    return QUESTION_TYPE.SQL_CODING;
  }
  if (
    enumRaw === QUESTION_TYPE.FRONTEND_CODING ||
    enumRaw.includes("frontend") ||
    data.Tabs
  ) {
    return QUESTION_TYPE.FRONTEND_CODING;
  }
  if (
    enumRaw === QUESTION_TYPE.PYTHON_CODING ||
    enumRaw === QUESTION_TYPE.CODING ||
    enumRaw === QUESTION_TYPE.PROJECT_CODING ||
    type === QUESTION_TYPE.CODING ||
    enumRaw.includes("python") ||
    enumRaw.includes("coding")
  ) {
    return QUESTION_TYPE.PYTHON_CODING;
  }
  if (data.options || data.correct_answer) return QUESTION_TYPE.MCQ;
  return "unknown";
};

export const isCodingPreviewKind = (
  kind: PreviewKind
): kind is CodingPreviewKind =>
  kind === QUESTION_TYPE.PYTHON_CODING ||
  kind === QUESTION_TYPE.SQL_CODING ||
  kind === QUESTION_TYPE.FRONTEND_CODING;

export const buildMcqPreview = (q: PreviewQuestion): McqPreviewModel | null => {
  const data = asRecord(q.question_data) || {};
  const options = asStringArray(data.options);
  const correct = asString(data.correct_answer || data.Ans);
  const question = asString(data.question || data.Qn);
  if (!question || options.length === 0) return null;
  return {
    question_id: q.question_id,
    question,
    options,
    correct_answer: correct,
    explanation: asString(data.Explanation || data.explanation) || undefined,
    topic_name: q.topic_name,
    sub_topic_name: q.sub_topic_name,
    level: q.level || asString(data.level) || undefined,
  };
};

export const buildCodingPreview = (
  q: PreviewQuestion,
  kind: CodingPreviewKind
): CodingPreviewModel | null => {
  const data = asRecord(q.question_data) || {};
  const prompt = asString(data.Qn || data.question);
  if (!prompt) return null;

  const examplesRaw = Array.isArray(data.Examples) ? data.Examples : [];
  const examples = examplesRaw
    .map((item) => {
      const row = asRecord(item);
      const example = asRecord(row?.Example);
      if (!example) return null;
      return {
        inputs: asStringArray(example.Inputs),
        output: asString(example.Output),
        explanation: asString(example.Explanation),
      };
    })
    .filter(Boolean) as CodingPreviewModel["examples"];

  const template = asString(data.Template || data.defaulttemplate);
  const answer = asString(data.Ans || data.Query);
  const functionCall = asString(data.FunctionCall);
  const initialCode =
    template && functionCall
      ? `${template}\n\n\n\n\n${functionCall}`
      : template || answer;

  const tablesRaw = Array.isArray(data.Tables) ? data.Tables : [];
  const tables = tablesRaw
    .map((item) => {
      const row = asRecord(item);
      if (!row) return null;
      const tab_name = asString(row.tab_name || row.name);
      const tableData = Array.isArray(row.data)
        ? (row.data as Record<string, unknown>[])
        : [];
      if (!tab_name) return null;
      return { tab_name, data: tableData };
    })
    .filter(Boolean) as CodingPreviewModel["tables"];

  const codeValidation = asRecord(data.Code_Validation) || {};

  const tabsRaw = Array.isArray(data.Tabs) ? data.Tabs : [];
  const tabs = tabsRaw
    .map((item) => {
      const row = asRecord(item);
      if (!row) return null;
      const name = asString(row.name || row.Name || row.filename);
      if (!name) return null;
      const validation = asRecord(codeValidation[name]);
      const content = asString(
        row.content ||
          row.Content ||
          row.code ||
          row.Ans ||
          row.template ||
          row.Template ||
          validation?.Ans ||
          validation?.template ||
          validation?.Template
      );
      const type = asString(row.type) || fileTypeFromName(name);
      return { name, content, type };
    })
    .filter(Boolean) as NonNullable<CodingPreviewModel["tabs"]>;

  const expectedOutputRaw = data.ExpectedOutput;
  const expectedOutput = Array.isArray(expectedOutputRaw)
    ? (expectedOutputRaw as Record<string, unknown>[])
    : [];

  const expectedImage = asString(
    data.ExpectedImage || data.image_path || data.ImagePath
  );
  const expectedVideo = asString(
    data.ExpectedVideo || data.video_path || data.VideoPath
  );
  const requirements = asString(data.requirements || data.Requirements);

  return {
    kind,
    question_id: q.question_id,
    prompt,
    template: initialCode || tabs[0]?.content || "",
    answer,
    topic_name: q.topic_name,
    sub_topic_name: q.sub_topic_name,
    level: q.level || asString(data.level) || undefined,
    examples,
    testCases: Array.isArray(data.TestCases) ? data.TestCases : [],
    expectedOutput,
    tables,
    table: asString(data.Table) || undefined,
    functionCall: functionCall || undefined,
    tabs: tabs.length > 0 ? tabs : undefined,
    requirements: requirements || undefined,
    Code_Validation:
      Object.keys(codeValidation).length > 0 ? codeValidation : undefined,
    image_urls: Array.isArray(data.image_urls)
      ? (data.image_urls as Array<{ actualUrl: string; expectedUrl: string }>)
      : undefined,
    ExpectedImage: expectedImage || undefined,
    ExpectedVideo: expectedVideo || undefined,
    ExpectedOutput: expectedOutputRaw,
  };
};

